/**
 * 开发票抽屉
 */
import React, { useEffect, useRef, memo } from 'react';
import { connect } from 'react-redux';
import { message, Spin, Button, Popconfirm, Modal } from 'antd';
import intl from 'react-intl-universal';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import PortalLoading from '@/components/PortalLoading';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import {
  initPageState,
  setLoadingStatus,
  getMainState,
  resetState,
} from '@/redux/actions/bill/invoice/index';
import { ReducerState, storeName } from '@/redux/reducers/bill/invoice';
import ThrottleButton from '@/components/Reservation/ThrottleButton';

import InvoiceOrderList from './components/InvoiceOrderList';
import InvoiceAmount from './components/InvoiceAmount';
import InvoiceInfos from './components/InvoiceInfos';
import useAddInvoiceOrderDrawer from './components/AddInvoiceOrderDrawer';
import { InvoiceOrderTypeEnum } from './constant';
import * as helper from './helper';
import styles from './index.module.scss';

const { confirm } = Modal;

interface IConfig {
  isView: boolean; // 仅查看、不能编辑
  orders?: Array<{ orderNo: string; orderType: string }>; // 开票订单
  logId?: any; // 仅查看时必须
  callback?: () => void;
}

const stateSelector = (state: ReducerState) => {
  const { isInit, isLoading, orderList } = state;
  return { isInit, isLoading, orderList };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callInitPage: initPageState,
  callSetLoading: setLoadingStatus,
  callGetState: getMainState,
  resetState,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IProps extends MapStateProps, MapDispatchProps, IConfig {
  unmountNode: () => void;
}

const Index: React.FC<IProps> = ($$) => {
  const { isView, unmountNode, callback, isInit, isLoading, callInitPage, callGetState } = $$;

  const addInvoiceOrderPortal = useAddInvoiceOrderDrawer();

  // 数据初始化
  useEffect(() => {
    if ($$.isView) {
      // getInvoicesSummaryDetail
      callInitPage(() => {
        return helper.getInvoicesSummaryDetail($$.logId).catch((e) => {
          unmountNode();
          throw e;
        });
      });
      return;
    }

    if ($$.orders && $$.orders.length > 0) {
      callInitPage(() => {
        return helper.getInvoicesSummary($$.orders).catch((e) => {
          unmountNode();
          throw e;
        });
      });
    }
  }, []);

  // 取消开发票
  const handleEditCancel = () => {
    $$.resetState();
    unmountNode();
  };

  // 提交开发票
  const handleSubmitClick = () => {
    // 二次确认 & 数据提交
    // eslint-disable-next-line consistent-return
    callGetState(async (state) => {
      // 数据校验
      const { access, msg, duration } = helper.checkSubmitInvoiceData(state);
      if (!access) {
        message.warning({
          content: msg,
          duration,
        });
        return { success: false };
      }

      confirm({
        title: intl.get('bill_invoice_modal_title'),
        icon: <ExclamationCircleOutlined />,
        onOk() {
          return helper
            .submitInvoiceData(state)
            .then((res) => {
              const { success } = res || {};
              if (success) {
                $$.resetState();
                unmountNode();
                if (typeof callback === 'function') callback();
              }
            })
            .catch((e) => {
              //
            });
        },
        // onCancel() {
        //   console.log('Cancel');
        // },
      });
    });
  };

  // 添加开票按钮
  const addInvoiceOrderBtns = () => {
    if (isView) return null;

    const addRoomOrder = () =>
      addInvoiceOrderPortal.showDrawer({ type: InvoiceOrderTypeEnum.room });
    const addOtherOrder = () =>
      addInvoiceOrderPortal.showDrawer({ type: InvoiceOrderTypeEnum.other });

    return (
      <div>
        <Button type="primary" style={{ marginRight: 16 }} onClick={addRoomOrder}>
          {intl.get('bill_invoice_modal_add_room_order_btn')}
        </Button>
        <Button type="primary" onClick={addOtherOrder}>
          {intl.get('bill_invoice_modal_add_other_order_btn')}
        </Button>
      </div>
    );
  };

  // if (!isInit) return <PortalLoading />;

  return (
    <Spin spinning={isLoading}>
      <DrawerHeader
        title={intl.get('bill_invoice_modal_invoice_info')}
        onClose={handleEditCancel}
      />
      <div className={styles.pageContainer}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <span>{intl.get('bill_invoice_modal_confirm_invoice_title')}</span>
            {addInvoiceOrderBtns()}
          </div>
          <InvoiceOrderList isView={isView} />
          <InvoiceAmount />
          <InvoiceInfos isView={isView} />
        </div>
      </div>
      <DrawerFooter>
        {!$$.isView ? (
          <ThrottleButton type="primary" className={styles.submitBtn} onClick={handleSubmitClick}>
            {intl.get('bill_invoice_modal_save')}
          </ThrottleButton>
        ) : null}

        <Button onClick={handleEditCancel}>
          {$$.isView ? intl.get('bill_invoice_modal_close') : intl.get('bill_invoice_modal_cancel')}
        </Button>
      </DrawerFooter>
    </Spin>
  );
};

export default () =>
  usePortalDrawer<IConfig>(connect(mapStateToProps, mapDispatchToProps)(memo(Index)));
